import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Loader2, MapPin, X } from "lucide-react";
import { api } from "../lib/api";
import { useAppSettings } from "../lib/useAppSettings";
import MaterialPhotos from "./MaterialPhotos";
import DestinationPicker, { destBody, emptyDest, resolveDest, useDestinations } from "./DestinationPicker";

const errText = (e, f) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || f; };

/**
 * FLD-13 — shake out whole rooms at once. Tick rooms in the Material grid and every uninstalled
 * unit ASSIGNED to those rooms comes along, wherever it is sitting right now (a bulk stash in the
 * storage closet included). Everything starts ticked with the room totals; open a product to
 * untick the rooms that are not moving.
 */
export default function RoomsMoveDialog({ projectId, actor, rooms = [], viewFilter, onClose, onDone }) {
  const keys = useMemo(() => new Set(rooms.map((r) => r.room_key)), [rooms]);
  const [rows, setRows] = useState(null);
  const [off, setOff] = useState({});                 // placement_id → excluded
  const [qty, setQty] = useState({});                 // placement_id → quantity
  const [open, setOpen] = useState({});
  // FT-2 §5 — the rooms were already ticked in the grid; re-listing every product was a second
  // recap of the same decision. What is moving is taken as given and folded behind one
  // disclosure, so the sheet is destination → ONE confirm.
  const [showWhat, setShowWhat] = useState(false);
  const [dest, setDest] = useState({ ...emptyDest, mode: "own_room" });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // FT-2 §6 — document where it landed when placement photos are on.
  const photosOn = useAppSettings().material_placement_photos === true;
  const [photoAt, setPhotoAt] = useState("");

  useEffect(() => {
    api.get("/material/placements/detail", { params: { project_id: projectId } })
      .then(({ data }) => {
        const mine = (data.placements || []).filter((p) => keys.has(p.owning_room_key || "")
          && (p.movable_qty || 0) > 0 && !(p.hold?.status || p.hold?.kind));
        setRows(mine);
        setQty(Object.fromEntries(mine.map((p) => [p.id, p.movable_qty])));
      })
      .catch(() => { toast.error("Could not read what is in those rooms"); setRows([]); });
  }, [projectId, keys]);

  const groups = useMemo(() => {
    const m = new Map();
    (rows || []).forEach((p) => {
      const k = `${p.description || p.product_code}|${p.uom}`;
      if (!m.has(k)) m.set(k, { key: k, description: p.description || p.product_code, uom: p.uom, lines: [] });
      m.get(k).lines.push(p);
    });
    return [...m.values()].map((g) => ({
      ...g,
      lines: g.lines.sort((a, b) => (a.room_label || "").localeCompare(b.room_label || "", undefined, { numeric: true })),
      total: g.lines.reduce((s, p) => s + p.movable_qty, 0),
      taking: g.lines.reduce((s, p) => s + (off[p.id] ? 0 : Number(qty[p.id] || 0)), 0),
    })).sort((a, b) => a.description.localeCompare(b.description, undefined, { numeric: true }));
  }, [rows, off, qty]);

  const entries = useMemo(() => (rows || []).filter((p) => !off[p.id] && Number(qty[p.id]) > 0)
    .map((p) => ({ placement_id: p.id, description: p.description || p.product_code, uom: p.uom,
                   available: p.movable_qty, qty: Number(qty[p.id]), from_location: p.location_label })), [rows, off, qty]);
  const data = useDestinations(projectId, entries.map((e) => e.placement_id), viewFilter);
  const resolved = useMemo(() => resolveDest(dest, data, entries), [dest, data, entries]);
  const okToMove = resolved.filter((r) => !r.flagged && !r.noop);
  const units = okToMove.reduce((s, r) => s + Number(r.qty || 0), 0);
  const byDest = useMemo(() => {
    const m = new Map();
    okToMove.forEach((r) => m.set(r.to_label, (m.get(r.to_label) || 0) + Number(r.qty || 0)));
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [okToMove]);

  const toggleGroup = (g) => {
    const allOn = g.lines.every((p) => !off[p.id]);
    setOff((c) => ({ ...c, ...Object.fromEntries(g.lines.map((p) => [p.id, allOn])) }));
    if (allOn) setOpen((c) => ({ ...c, [g.key]: true }));      // show which rooms to bring back
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/material/move", {
        project_id: projectId, by: actor, note, confirm: true, dest: destBody(dest, data),
        entries: okToMove.map((r) => ({ placement_id: r.placement_id, qty: Number(r.qty), room_key: r.to_room_key || "" })) });
      toast.success(`${res.units} unit(s) moved`, { description: res.message });
      await onDone?.();
      if (photosOn && byDest.length === 1) { setPhotoAt(byDest[0][0]); return; }
      onClose?.();
    } catch (e) { toast.error(errText(e, "Nothing was moved — the move was refused")); }
    finally { setBusy(false); }
  };

  const total = groups.reduce((s, g) => s + g.total, 0);
  return (
    <div className="fixed inset-0 z-[140] bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="rooms-move-dialog">
      {photoAt && (
        <MaterialPhotos projectId={projectId} locationLabel={photoAt} actor={actor}
                        onClose={() => { setPhotoAt(""); onClose?.(); }}
                        onChanged={() => onDone?.()} />
      )}
      <div className="bg-white w-full sm:max-w-2xl max-h-[94vh] rounded-t-lg sm:rounded-lg flex flex-col">
        <div className="shrink-0 px-3 py-2 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-blue-700" />
            <b className="text-[15px] sm:text-sm">Move the material in {rooms.length} room(s)</b>
            <button onClick={onClose} aria-label="Close" className="ml-auto min-h-[44px] min-w-[44px] inline-flex items-center justify-center" data-testid="rooms-move-close"><X className="w-5 h-5" /></button>
          </div>
          <div className="text-[11px] text-gray-600" data-testid="rooms-move-rooms">
            {rooms.map((r) => r.label).join(" · ")}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2 space-y-2">
          {rows === null ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
            <>
              <div className="text-[12px] text-gray-700 flex flex-wrap items-center gap-2" data-testid="rooms-move-total">
                <span>
                  <b className="tabular-nums">{total}</b> uninstalled unit(s) in {rooms.length} ticked
                  room(s) are moving. Nothing else is touched.
                </span>
                <button type="button" onClick={() => setShowWhat((v) => !v)}
                        className="min-h-[36px] px-2 text-[12px] underline text-blue-700"
                        data-testid="rooms-move-what-toggle">
                  {showWhat ? "Hide what is moving" : `Change what is moving (${groups.length} product(s))`}
                </button>
              </div>
              {groups.length === 0 && <div className="py-6 text-center text-sm text-gray-500" data-testid="rooms-move-empty">Nothing uninstalled is assigned to those rooms.</div>}
              {showWhat && (
              <ul className="divide-y divide-gray-100" data-testid="rooms-move-what">
                {groups.map((g) => {
                  const allOn = g.lines.every((p) => !off[p.id]);
                  const some = g.lines.some((p) => !off[p.id]) && !allOn;
                  return (
                    <li key={g.key} className="py-1.5" data-testid={`rooms-move-group-${g.key.replace(/[^\w]+/g, "_")}`}>
                      <div className="flex items-center gap-2 text-[13px]">
                        <input type="checkbox" checked={allOn} ref={(el) => { if (el) el.indeterminate = some; }}
                               onChange={() => toggleGroup(g)} className="w-4 h-4"
                               data-testid={`rooms-move-check-${g.key.replace(/[^\w]+/g, "_")}`} />
                        <button type="button" onClick={() => setOpen((c) => ({ ...c, [g.key]: !c[g.key] }))}
                                className="flex-1 text-left inline-flex items-center gap-1 min-h-[40px] sm:min-h-0"
                                data-testid={`rooms-move-open-${g.key.replace(/[^\w]+/g, "_")}`}>
                          {open[g.key] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          <b className="leading-snug">{g.description}</b>
                          <span className="text-gray-500 text-[11px]">{g.uom} · {g.lines.length} room line(s)</span>
                        </button>
                        <span className="tabular-nums text-[13px]" data-testid={`rooms-move-qty-${g.key.replace(/[^\w]+/g, "_")}`}>
                          <b>{g.taking}</b><span className="text-gray-400">/{g.total}</span>
                        </span>
                      </div>
                      {open[g.key] && (
                        <ul className="mt-1 ml-6 space-y-1">
                          {g.lines.map((p) => (
                            <li key={p.id} className="flex flex-wrap items-center gap-2 text-[12px]" data-testid={`rooms-move-line-${p.id}`}>
                              <input type="checkbox" checked={!off[p.id]} className="w-4 h-4"
                                     onChange={() => setOff((c) => ({ ...c, [p.id]: !c[p.id] }))}
                                     data-testid={`rooms-move-line-check-${p.id}`} />
                              <span className="flex-1 min-w-[140px]">
                                <b>{p.room_label || "(no room)"}</b>
                                <span className="text-gray-500"> · now at {p.location_label || "—"}</span>
                              </span>
                              <Input type="number" min="0" max={p.movable_qty} step="any" value={qty[p.id] ?? p.movable_qty}
                                     disabled={!!off[p.id]} className="h-10 sm:h-8 w-20 text-right"
                                     data-testid={`rooms-move-line-qty-${p.id}`}
                                     onChange={(e) => setQty((c) => ({ ...c, [p.id]: Math.min(Number(e.target.value), p.movable_qty) }))} />
                              <span className="text-gray-400 tabular-nums w-10">of {p.movable_qty}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
              )}

              <div className="text-[11px] uppercase tracking-wide text-gray-500">Where is it going?</div>
              <DestinationPicker data={data} entries={entries} value={dest} onChange={setDest} testId="rooms-move-dest" />

              <div className="rounded border border-gray-200 bg-slate-50 p-2 text-[12px] space-y-0.5" data-testid="rooms-move-preview">
                <div><b data-testid="rooms-move-units">{units}</b> unit(s) · {okToMove.length} entry(ies) → <b>{byDest.length}</b> destination(s)</div>
                {byDest.slice(0, 12).map(([label, q]) => <div key={label} data-testid={`rooms-move-dest-${label.replace(/[^\w]+/g, "_")}`}>{label}: <b className="tabular-nums">{q}</b></div>)}
                {resolved.some((r) => r.noop) && <div className="text-gray-600">{resolved.filter((r) => r.noop).length} already in that location — skipped.</div>}
                {resolved.some((r) => r.flagged) && <div className="text-amber-900" data-testid="rooms-move-flagged">{resolved.filter((r) => r.flagged).length} entry(ies) still need a destination and are not included.</div>}
                {okToMove.some((r) => r.staging) && <div className="text-amber-900">Staging location — not spread into assigned rooms.</div>}
                <div className="text-gray-600">Stays on site and <b>Not Installed</b>. FRS and installed quantities are untouched.</div>
              </div>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="h-10" data-testid="rooms-move-note" />
            </>
          )}
        </div>

        <div className="shrink-0 border-t border-gray-200 px-3 py-2 flex flex-wrap gap-1 items-center">
          <Button className="min-h-[48px] sm:min-h-[40px] flex-1 sm:flex-none" disabled={busy || units <= 0} onClick={confirm} data-testid="rooms-move-confirm">
            Confirm Move — {units} unit(s)
          </Button>
          <Button variant="ghost" className="min-h-[48px] sm:min-h-[40px]" onClick={onClose} data-testid="rooms-move-cancel">Cancel</Button>
        </div>
      </div>
    </div>
  );
}
